import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import ServiceLayout from '../../../components/layout/ServiceLayout';
import ServicePageSection from '../../../components/layout/ServicePageSection';

// UI components
import CompactFeatureCard from '../../../components/ui/CompactFeatureCard';
import ProcessStep from '../../../components/ui/ProcessStep';
import Card from '../../../components/ui/Card';
import FAQ from '../../../components/ui/FAQ';

// Section components
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { caseStudies, faqs } from '../../../data/mockData';

/**
 * Custom AI Development Service Page - Clean and Professional Layout
 */
const CustomAIDevelopmentPage: React.FC = () => {
  // Filter relevant case studies
  const relevantCaseStudies = caseStudies.filter(
    (study) => study.solution === 'Custom AI Development'
  );

  // Filter relevant FAQs
  const relevantFaqs = faqs.filter((_, index) => [0, 2, 4].includes(index));

  const keyBenefits = [
    {
      title: "Precision-Fit Solutions",
      description: "Unlike off-the-shelf AI tools, custom solutions are designed specifically for your unique processes, challenges, and objectives, delivering maximum business impact.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      )
    },
    {
      title: "Competitive Advantage",
      description: "Develop proprietary AI capabilities that differentiate your business from competitors who rely on standard tools available to everyone in your industry.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      )
    },
    {
      title: "Seamless Integration",
      description: "Custom-built AI agents integrate perfectly with your existing systems and data infrastructure, eliminating compatibility issues and data silos.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1V7a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V4a1 1 0 011-1h3a1 1 0 011 1v1z" />
        </svg>
      )
    }
  ];

  const developmentProcess = [
    {
      step: 1,
      title: "Discovery & Requirements Analysis",
      description: "We work closely with your team to deeply understand your business challenges, processes, data assets, and success criteria for the AI solution."
    },
    {
      step: 2,
      title: "Data Assessment & Strategy",
      description: "Our data scientists evaluate your available data and develop a strategy for data preparation, augmentation, and feature engineering to build effective AI models."
    },
    {
      step: 3,
      title: "Solution Architecture & Design",
      description: "We design a comprehensive AI solution architecture that addresses your specific requirements while ensuring scalability, security, and maintainability."
    },
    {
      step: 4,
      title: "AI Model Development & Training",
      description: "Our AI engineers develop and train custom models using your data, applying the most appropriate algorithms and techniques for your specific use case."
    },
    {
      step: 5,
      title: "Integration & Deployment",
      description: "We integrate the AI solution with your existing systems and deploy it in your preferred environment, whether cloud-based, on-premises, or hybrid."
    },
    {
      step: 6,
      title: "Testing, Optimization & Handover",
      description: "Rigorous testing ensures your AI solution performs optimally. We fine-tune models based on real-world performance data before a complete handover to your team."
    }
  ];

  const solutionTypes = [
    {
      title: "Intelligent Assistants & Chatbots",
      description: "Domain-specific conversational agents that understand your business context, customer needs, and company knowledge base to provide accurate assistance."
    },
    {
      title: "Predictive Analytics Systems",
      description: "AI models that analyze your historical data to forecast trends, identify opportunities, and predict outcomes specific to your business drivers."
    },
    {
      title: "Computer Vision Solutions",
      description: "Custom image and video analysis systems for applications like quality control, inventory management, security monitoring, and more."
    },
    {
      title: "Natural Language Processing Tools",
      description: "Text analysis solutions for document processing, sentiment analysis, content generation, and knowledge extraction tailored to your industry terminology."
    },
    {
      title: "Recommendation Engines",
      description: "Personalized recommendation systems for products, content, or services based on your unique customer data and business rules."
    },
    {
      title: "Decision Support Systems",
      description: "AI-powered tools that analyze complex situations and provide data-driven recommendations to support critical business decisions."
    }
  ];

  const techStacks = [
    {
      category: "Machine Learning Frameworks",
      technologies: ["TensorFlow & Keras", "PyTorch", "Scikit-learn", "Hugging Face Transformers", "LangChain"]
    },
    {
      category: "Cloud Platforms",
      technologies: ["AWS SageMaker", "Google AI Platform", "Azure AI Services", "IBM Watson", "Specialized AI APIs"]
    },
    {
      category: "Development Tools",
      technologies: ["Python Ecosystem", "MLOps Tools", "Docker & Kubernetes", "CI/CD for AI", "Model Monitoring Tools"]
    }
  ];

  return (
    <ServiceLayout>
      <Hero
        title="Custom AI Agent Development"
        subtitle="Tailor-made AI solutions designed specifically for your unique business challenges and opportunities."
        ctaText="Discuss Your Custom AI Needs"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Key Benefits Section */}
      <ServicePageSection 
        title="Why Choose Custom AI Development" 
        bgColor="gray"
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {keyBenefits.map((benefit, index) => (
            <CompactFeatureCard
              key={benefit.title}
              title={benefit.title}
              description={benefit.description}
              icon={benefit.icon}
              delay={index * 0.1}
            />
          ))}
        </div>
      </ServicePageSection>

      {/* Our Development Process Section */}
      <ServicePageSection 
        title="Our Custom AI Development Process" 
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {developmentProcess.map((process, index) => (
            <motion.div
              key={process.step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <ProcessStep 
                number={process.step} 
                title={process.title}
                description={process.description}
              />
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Types of Custom AI Solutions */}
      <ServicePageSection 
        title="Types of Custom AI Solutions We Develop" 
        bgColor="gray"
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {solutionTypes.map((solution, index) => (
            <motion.div
              key={solution.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full p-6 hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-lg font-semibold mb-3">{solution.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{solution.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Technologies We Use */}
      <ServicePageSection 
        title="Our AI Development Stack" 
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {techStacks.map((stack, index) => (
            <motion.div
              key={stack.category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full p-6">
                <h3 className="text-lg font-semibold mb-4">{stack.category}</h3>
                <ul className="space-y-2">
                  {stack.technologies.map((tech, idx) => (
                    <li key={idx} className="flex items-center text-sm text-neutral-600">
                      <svg className="w-4 h-4 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                      </svg>
                      {tech}
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Case Studies Section */}
      {relevantCaseStudies.length > 0 && (
        <ServicePageSection title="Custom AI Success Stories" bgColor="gray" padding="md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {relevantCaseStudies.slice(0, 2).map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={study.url} className="block h-full">
                  <Card hover className="h-full p-6">
                    <div className="mb-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {study.solution}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">{study.title}</h3>
                    <p className="text-neutral-600 text-sm mb-4">{study.excerpt}</p>
                    
                    <div className="space-y-2">
                      {study.results.slice(0, 2).map((result, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <svg className="w-4 h-4 text-primary mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-neutral-600">{result}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-neutral-100">
                      <span className="inline-flex items-center text-primary font-medium text-sm">
                        Read full case study
                        <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <Link 
              to="/case-studies" 
              className="inline-flex items-center text-primary font-medium hover:text-primary-700 transition-colors"
            >
              View all case studies
              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </ServicePageSection>
      )}

      {/* FAQ Section */}
      <ServicePageSection 
        title="Frequently Asked Questions" 
        padding="md"
      >
        <div className="max-w-3xl mx-auto space-y-6">
          {relevantFaqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <FAQ items={[{ question: faq.question, answer: faq.answer }]} />
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Call to Action */}
      <CallToAction
        title="Ready to build your custom AI solution?"
        description="Contact us today to discuss your business challenges and how a custom AI solution can transform your operations and create competitive advantage."
        ctaText="Start Your AI Project"
        ctaLink="/contact"
      />
    </ServiceLayout>
  );
};

export default CustomAIDevelopmentPage;